// ============================================================================
// Couleurs d'une SKILLCARD (migration 0274) — PUR, testé.
// ============================================================================
// Une teinte par niveau, du violet de la 6ème au rouge de la Terminale : la
// carte prend la teinte de son niveau le PLUS BAS. Le tuteur peut forcer le
// « bleu nuit » historique (aussi le repli d'une carte sans niveau).
//
// `accent` colore la bordure (recto ET verso), les puces, le cadre de la
// formule, la tuile du QR et la créature origami ; `bg` est le fond du verso.
// Couleurs LITTÉRALES (une carte imprimée ne suit pas le thème de l'app).
// ============================================================================

import { SCHOOL_LEVELS } from '../index';
import type { SchoolLevel } from '../types';
import type { CardColorTheme } from './schemas';

export interface CardPalette {
  /** Niveau qui donne la teinte, ou `night`. */
  key: SchoolLevel | 'night';
  accent: string;
  bg: string;
  /** Nom de la teinte (« violet », « bleu nuit »…). */
  label: string;
}

/** Le verso historique : bleu nuit + or. */
export const CARD_NIGHT_PALETTE: CardPalette = {
  key: 'night',
  accent: '#F4C64E',
  bg: '#0C1C4A',
  label: 'bleu nuit',
};

export const CARD_LEVEL_PALETTES: Record<SchoolLevel, CardPalette> = {
  '6e': { key: '6e', accent: '#B76CFF', bg: '#230F3A', label: 'violet' },
  '5e': { key: '5e', accent: '#7B74FF', bg: '#14123A', label: 'indigo' },
  '4e': { key: '4e', accent: '#4DA3FF', bg: '#0A1B33', label: 'bleu' },
  '3e': { key: '3e', accent: '#3DDC97', bg: '#0B2A1E', label: 'vert' },
  '2nde': { key: '2nde', accent: '#FFD84D', bg: '#262008', label: 'jaune' },
  '1re': { key: '1re', accent: '#FF9F43', bg: '#2A1A0C', label: 'orange' },
  tle: { key: 'tle', accent: '#FF6B6B', bg: '#2A0F18', label: 'rouge' },
  // Prépa (migration 0302) : UNE seule couleur pour toutes les filières
  // (décision Philippe) = le bleu nuit / or historique.
  mpsi: CARD_NIGHT_PALETTE,
  ptsi: CARD_NIGHT_PALETTE,
  mp: CARD_NIGHT_PALETTE,
  pt: CARD_NIGHT_PALETTE,
};

/** Niveau le plus bas (ordre scolaire), ou `null` si aucun niveau connu. */
export function lowestCardLevel(levels: readonly string[]): SchoolLevel | null {
  const order = SCHOOL_LEVELS.map((l) => l.value as string);
  const idx = levels.map((l) => order.indexOf(l)).filter((i) => i >= 0);
  if (idx.length === 0) return null;
  return SCHOOL_LEVELS[Math.min(...idx)]!.value;
}

/** Palette d'une carte : teinte du niveau le plus bas, sinon bleu nuit. */
export function cardPalette(
  levels: readonly string[],
  colorTheme: CardColorTheme | null | undefined = 'level',
): CardPalette {
  if (colorTheme === 'night') return CARD_NIGHT_PALETTE;
  const lowest = lowestCardLevel(levels);
  return lowest ? CARD_LEVEL_PALETTES[lowest] : CARD_NIGHT_PALETTE;
}
